import {
  Control,
  useController,
  UseFormReset,
  UseFormSetValue,
} from 'react-hook-form';
import { useTaskToName } from '../../../hooks/useTaskToName/useTaskToName';
import {
  InvoiceStatus,
  PaymentTerms,
  S3UploadLevel,
  Task,
  TaskDirection,
  TaskSettlementStatus,
  TaskType,
} from '@admiin-com/ds-graphql';
import { DateTime } from 'luxon';
import { PaymentFrequency } from '@admiin-com/ds-graphql';
import { getSignersFromAnnotations } from '../../../helpers/signature';
import { S3Upload, S3UploadType } from '@admiin-com/ds-common';
import { getFileNameFromKey } from '@admiin-com/ds-amplify-web';
import { transformLineItemToDocumnetAnalysisInput } from '../../../components/PayBill/useBillCreate/transformer';
import { gql, useMutation } from '@apollo/client';
import { createTaskDocumentUrl as CREATE_TASK_DOCUMENT_URL } from '@admiin-com/ds-graphql';
import {
  DATE_FORMATS,
  S3Upload as S3UploadCommon,
} from '@admiin-com/ds-common';
export const useResetTaskContextFromTask = ({
  setValue,
  reset,
  append,
}: {
  setValue: UseFormSetValue<any>;
  reset: UseFormReset<any>;
  append?: any;
}) => {
  const getTaskName = useTaskToName();

  const getDocuments = async (task: Task) => {
    if (task && task.documents && task.documents.length > 0) {
      const documentUrlData = await createTaskDocumentUrl({
        variables: {
          input: {
            taskId: task.id,
            entityId: task.entityId,
          },
        },
      });
      const url = documentUrlData?.data?.createTaskDocumentUrl?.url;

      if (url) {
        return task.documents.map(
          (document: S3Upload | null): S3UploadCommon | null => ({
            identityId: document?.identityId,
            key: document?.key ?? '',
            src: url,
            level: document?.level as S3UploadLevel,
            type: document?.type as S3UploadType,
          })
        );
      }
    }
    return [];
  };
  const [createTaskDocumentUrl] = useMutation(gql(CREATE_TASK_DOCUMENT_URL));

  const createTaskData = async (task: Task) => {
    const [
      { data: from },
      { data: to },
      { data: futureTaskFrom },
      { data: futureTaskTo },
    ] = await Promise.all([
      getTaskName(task?.fromId, true),
      getTaskName(task?.toId, true),
      getTaskName(task?.futureLinkedTask?.fromId, true),
      getTaskName(task?.futureLinkedTask?.toId, true),
    ]);
    const documents = await getDocuments(task);
    return {
      id: task.id,
      entityId: task.entityId,
      futureTaskInput: task.futureLinkedTask
        ? {
            amount: task?.futureLinkedTask?.amount ?? 0,
            dueAt:
              task?.futureLinkedTask?.dueAt ??
              DateTime.now().toFormat(DATE_FORMATS.BACKEND_DATE),
            reference: task?.futureLinkedTask?.reference ?? '',
            issuedAt: task?.issuedAt ?? '',
            bpayReferenceNumber:
              task?.futureLinkedTask?.bpayReferenceNumber ?? '',
            paymentFrequency:
              task?.futureLinkedTask?.paymentFrequency ?? PaymentFrequency.ONCE,
            lineItems:
              task?.futureLinkedTask?.lineItems?.map((lineItem, index) => ({
                ...transformLineItemToDocumnetAnalysisInput(lineItem),
              })) ?? [],
            numberOfPayments: task?.futureLinkedTask?.numberOfPayments ?? 1,
            from: futureTaskFrom,
            to: futureTaskTo,
          }
        : undefined,
      reference: task?.reference ?? '',
      bpayReferenceNumber: task?.bpayReferenceNumber ?? '',
      notes: task?.notes ?? '',
      gstInclusive: false,
      paymentFrequency: task.paymentFrequency ?? PaymentFrequency.ONCE,
      dueAt: task?.dueAt ?? DateTime.now().toFormat(DATE_FORMATS.BACKEND_DATE),
      from: from,
      sendEmail: false,
      direction: task?.direction ?? TaskDirection.SENDING,
      to: to,
      amount: task?.amount?.toString() ?? '',
      lodgementAt: task?.lodgementAt ?? '',
      paymentAt: task?.paymentAt ?? '',
      documents: documents,
      annotations: task?.annotations ?? null,
      documentType: task?.documentType,
      settlementStatus:
        task?.settlementStatus ?? TaskSettlementStatus.REFUNDABLE,
      firstPaymentAt: task?.paymentAt ?? '',
      numberOfPayments: task?.numberOfPayments ?? 1,
      invoiceStatus: task?.invoiceStatus ?? InvoiceStatus.INVOICE,
      lineItems:
        task?.lineItems?.map((lineItem, index) => ({
          ...lineItem,
          id: (index + 1).toString(),
        })) ?? [],
      supportDocuments:
        task?.supportDocuments?.map((doc) => ({
          identityId: doc?.identityId ?? null,
          key: doc?.key ?? '',
          level: doc?.level as S3UploadLevel,
          type: doc?.type as S3UploadType,
          name:
            doc?.name || getFileNameFromKey(doc?.key ?? '') || doc?.key || '',
        })) ?? [],
      type: task?.type ?? TaskType.SIGN_ONLY,
      sendSMS: task.sendSMS ?? false,
      sendTemplate: task.sendTemplate ?? false,
      templateJson: task.templateJson ?? undefined,
      templateHtml: task.templateHtml ?? undefined,
      includeLetterhead: task.includeLetterhead ?? undefined,
      signers: getSignersFromAnnotations(task),
      sendCopyToContact: true,
      automaticGenerateInvoice: task.automaticGenerateInvoice ?? false,
      automaticInvoicePaymentAt: task.automaticInvoicePaymentAt ?? '',
      automaticInvoicePaymentTerms:
        task.automaticInvoicePaymentTerms ?? PaymentTerms.FORTNIGHT,
      otherRecipients:
        task?.otherRecipients?.map((recipient) => ({
          email: recipient?.email ?? '',
          sendOnCompletion: recipient?.sendOnCompletion ?? true,
        })) ?? [],
      quoteRequiresSignature: task.quoteRequiresSignature ?? false,
    };
  };

  const setTaskData = async (task: Task, name?: string) => {
    const namePrefix = !name ? `` : `${name}.`;
    if (!name) {
      reset(await createTaskData(task));
    } else {
      if (append) {
        append(await createTaskData(task));
      } else {
        setValue(namePrefix, createTaskData(task));
      }
    }
  };

  return setTaskData;
};
